import { useMemo } from 'react';
import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type {
	EditorContentFragmentResult,
	EditorContentFragmentValue,
} from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';

import type { useEditorByLineFragment_content$key } from './__generated__/useEditorByLineFragment_content.graphql.ts';
import type { useEditorByLineFragment_query$key } from './__generated__/useEditorByLineFragment_query.graphql.ts';

// The ContentHeaderUnifiedQuery node fields LivePagesByLine reads, for EditorContentUnifiedQuery.
const editorByLineFragmentNode: GraphQLTaggedNode = graphql`
	fragment useEditorByLineFragment_content on Content
	@argumentDefinitions(
		includeClassification: { type: "Boolean!", defaultValue: false }
		useClassificationLevelV2: { type: "Boolean!", defaultValue: false }
	)
	@catch {
		id
		type
		history {
			createdBy {
				displayName
				... on KnownUser {
					accountId
				}
				profilePicture {
					path
				}
			}
			ownedBy {
				displayName
				... on KnownUser {
					accountId
				}
				profilePicture {
					path
				}
			}
		}
		version {
			number
			when
		}
		contentProperties {
			latest {
				generatedBy
			}
		}
		space {
			id
			name
		}
		...BylineClassification_content
			@arguments(useClassificationLevelV2: $useClassificationLevelV2)
			@alias(as: "bylineClassificationFragment")
			@include(if: $includeClassification)
	}
`;

// Its contentContributors selection, on Query. The limit is DEFAULT_CONTRIBUTORS_FETCH_LIMIT.
const editorByLineContributorsFragmentNode: GraphQLTaggedNode = graphql`
	fragment useEditorByLineFragment_query on Query
	@argumentDefinitions(contentId: { type: "ID!" })
	@catch {
		contentContributors(id: $contentId, limit: 4) {
			count
			isOwnerContributor
		}
	}
`;

export type EditorByLineFragmentData = {
	content: {
		nodes: readonly [EditorContentFragmentValue<useEditorByLineFragment_content$key>];
	};
	contentContributors?: EditorContentFragmentValue<useEditorByLineFragment_query$key>['contentContributors'];
};

/**
 * The editor byline data from EditorContentUnifiedQuery in the shape of ContentHeaderUnifiedQuery
 * data. When it is provided, LivePagesByLine skips ContentHeaderUnifiedQuery.
 */
export function useEditorByLineFragment({
	contentId,
}: {
	contentId: string;
}): EditorContentFragmentResult<EditorByLineFragmentData> {
	const content = useEditorContentFragment<useEditorByLineFragment_content$key>(
		'editorByLineFragment',
		editorByLineFragmentNode,
		{ contentId },
	);
	const contributors = useEditorContentFragment<useEditorByLineFragment_query$key>(
		'editorByLineContributorsFragment',
		editorByLineContributorsFragmentNode,
		{ contentId },
	);

	return useMemo(
		() => ({
			isProvided: content.isProvided,
			loading: content.loading,
			data: content.data && {
				content: { nodes: [content.data] },
				contentContributors: contributors.data?.contentContributors,
			},
			error: content.error ?? contributors.error,
		}),
		[content, contributors],
	);
}
